// Copyright (c) StrangeBee 2023

import { isPeriodValue, periods } from '@Organisms/Dashboard/DashboardPanel/DashboardPeriod/DashboardPeriod';
import {
    DashboardInput,
    DashboardOutput,
    CustomizeOutput,
    WidgetOptionsOutput,
    WidgetSeriesOutput,
    SearchFormInterface,
    ObjectTypeV0ToV1,
    DescribeOutput,
} from 'thehive-sdk';
import moment from 'moment';
import { SecurityColors } from '@Constants/colors';

const parseOperator = (operator?: string): string => {
    if (operator === 'none') {
        return 'not';
    }

    if (operator === 'empty') {
        return 'is empty';
    }

    return operator || 'any';
};

const findSecurityColors = (field: string, describeData?: DescribeOutput): CustomizeOutput[] | undefined => {
    if (!['tlp', 'pap', 'severity'].includes(field)) {
        return undefined;
    }

    const describeField = describeData?.case.attributes.find(({ name }) => name === field);

    return describeField?.labels.map((label, index) => ({
        label,
        color: SecurityColors[label],
        name: describeField?.values[index],
    }));
};

const DashboardV0ToV1: Partial<Record<string, Record<string, string>>> = {
    default: {
        createdAt: '_createdAt',
        createdBy: '_createdBy',
        updatedAt: '_updatedAt',
        updatedBy: '_updatedBy',
        id: '_id',
        caseId: 'number',
        owner: 'assignee',
        resolutionStatus: 'status',
    },
    customField: {
        name: 'displayName',
        reference: 'name',
    },
};

const convertFieldToV1 = (entity = 'default', field = ''): string | undefined =>
    field?.startsWith('metrics')
        ? field?.replace('metrics', 'customFields')
        : field === 'status'
        ? 'stage'
        : DashboardV0ToV1[entity]?.[field] || DashboardV0ToV1['default']?.[field] || field || undefined;

type FilterV0 = {
    field: string;
    type: string;
    value: {
        list?: {
            text: string | number;
            label: string;
        }[];
        value?: number | string;
        operator?: string;
    };
};

type WidgetOptionsOutputV0 = Omit<WidgetOptionsOutput, 'series' | 'filters'> & {
    filters: FilterV0[];
    series: (Omit<WidgetSeriesOutput, 'filters'> & { filters: FilterV0[] })[];
    names: Record<string, string>;
    colors: Record<string, string>;
};

const parseCustomPeriod = (
    period: string,
    from?: number,
    to?: number,
): DashboardOutput['definition']['customPeriod'] => {
    if (!isPeriodValue(period) || period === 'all') {
        return undefined;
    }

    const today = moment();
    const newFrom = from || periods[period].from?.(today).valueOf();
    const newTo = to || today.valueOf();

    return {
        fromDate: newFrom,
        toDate: newTo,
    };
};

const parseValue = (value: FilterV0['value'], field?: FilterV0['field']): any[] => {
    if (field === 'status' && 'list' in value) {
        const values = value.list?.map(({ text }) => text) || [];
        return values.reduce((acc, value) => {
            if (value === 'Open') {
                return [...acc, 'New', 'InProgress'];
            }

            if (value === 'Resolved') {
                return [...acc, 'Closed'];
            }
            return acc;
        }, [] as string[]);
    }

    if (value === undefined || value === null) {
        return [];
    }

    if (typeof value === 'boolean') {
        return [value];
    }

    if ('value' in value) {
        return [value];
    }

    if ('list' in value) {
        return value.list?.map(({ text }) => text) || [];
    }

    return [];
};

const parseFilters = (entity = 'default', filters: FilterV0[] = []): SearchFormInterface[] =>
    filters.map(({ field, type, value }) => ({
        field: convertFieldToV1(entity, field) || '',
        type,
        values: parseValue(value, field),
        operator: parseOperator(value?.operator),
        describeAttribute: '' as any,
    }));

const parseOptions = (options: WidgetOptionsOutputV0, describeData?: DescribeOutput): WidgetOptionsOutput => ({
    ...options,
    entity: ObjectTypeV0ToV1[options.entity],
    title: options.title || 'No title',
    dateField: convertFieldToV1(options.entity, options.dateField || options.field),
    field: convertFieldToV1(options.entity, options.field),
    filters: parseFilters(options.entity, options.filters),
    periodDateField: '_createdAt',
    series: options.series?.map((serie) => ({
        ...serie,
        field: convertFieldToV1(options.entity, serie.field),
        dateField: convertFieldToV1(options.entity, serie.dateField),
        filters: parseFilters(options.entity, serie.filters),
    })),
    customize:
        options.colors && options.names
            ? Object.entries(options.names).map(([key, label]) => ({
                  name: key,
                  label,
                  color: options.colors[key],
              }))
            : findSecurityColors(options.field || '', describeData),
});

const parseLineOptions = ({ filters = [], ...options }: WidgetOptionsOutputV0): WidgetOptionsOutput => ({
    ...options,
    query: undefined,
    field: undefined,
    dateField: convertFieldToV1(options.entity, options.dateField || options.field),
    filters: parseFilters(options.entity, filters),
    title: options.title || 'No title',
    series: options.series?.map((serie) => ({
        ...serie,
        query: {},
        field: convertFieldToV1(options.entity, serie.field),
        dateField: convertFieldToV1(options.entity, serie.dateField || options.dateField || options.field),
        filters: parseFilters(serie.entity, serie.filters || []),
        periodDateField: '_createdAt',
        entity: ObjectTypeV0ToV1[serie.entity || options.entity],
    })),
});

export const convertOldDashboard = (
    { definition, ...dashboard }: DashboardInput | DashboardOutput,
    describeData?: DescribeOutput,
): DashboardInput | DashboardOutput | null => {
    const definitionParsed =
        typeof definition === 'string' ? (JSON.parse(definition) as DashboardOutput['definition']) : definition;

    if (!definitionParsed) {
        return null;
    }

    const { customPeriod } = definitionParsed;
    return {
        title: dashboard.title,
        description: dashboard.description,
        version: 1,
        definition: {
            items: definitionParsed.items.map(({ items, type }) => ({
                type,
                items: items.map((widget) => ({
                    ...widget,
                    type: (widget.type as any) === 'multiline' ? 'line' : widget.type,
                    options: ['line', 'multiline'].includes(widget.type)
                        ? parseLineOptions(widget.options as any)
                        : parseOptions(widget.options as any, describeData),
                })),
            })),
            period: definitionParsed.period,
            customPeriod: parseCustomPeriod(
                definitionParsed.period,
                typeof customPeriod?.fromDate === 'string'
                    ? new Date(customPeriod?.fromDate).valueOf()
                    : customPeriod?.fromDate,
                typeof customPeriod?.toDate === 'string'
                    ? new Date(customPeriod?.toDate).valueOf()
                    : customPeriod?.toDate,
            ),
        },
    };
};
